/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React from 'react';
import { useTheme } from '../../theme/ThemeContext';

function PlatformSection() {
    const { theme } = useTheme();

    const features = [
        {
            title: 'BaaS Core',
            description: 'Headless Django backend with secure, scalable PostgreSQL storage.'
        },
        {
            title: 'Studio',
            description: 'Visual builder to define models, forms, and permission rules.'
        },
        {
            title: 'Client Runtime',
            description: 'Instant React frontend that adapts to your defined schema.'
        },
        {
            title: 'File Store',
            description: 'Dedicated async file storage and management system.'
        }
    ];

    return (
        <section id="about" className="platform-section" style={{ padding: '100px 0', background: '#fafafa' }}>
            <div className="container">
                <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                    <p style={{
                        color: theme.secondary,
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        textTransform: 'uppercase',
                        letterSpacing: '2px',
                        marginBottom: '16px'
                    }}>
                        ARCEE PLATFORM
                    </p>
                    <h2 style={{
                        fontSize: '2.75rem',
                        fontWeight: 800,
                        color: theme.primary,
                        marginBottom: '24px',
                        lineHeight: 1.2
                    }}>
                        Enterprise Control.<br />No-Code Speed.
                    </h2>
                    <p style={{
                        fontSize: '1.15rem',
                        color: '#555',
                        maxWidth: '900px',
                        margin: '0 auto',
                        lineHeight: 1.8
                    }}>
                        Most no-code tools are complex. <strong>arcee is open and simplified nirvana.</strong> We decoupled the backend from the frontend to give you true architectural freedom. Design your data models in our Studio, and we provision a production-ready React frontend, PostgreSQL database and secure SaaS APIs instantly.
                    </p>
                </div>

                {/* Stats */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '100px', marginBottom: '70px', flexWrap: 'wrap' }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            fontSize: '4.5rem',
                            fontWeight: 900,
                            background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            marginBottom: '12px'
                        }}>10x</div>
                        <div style={{ fontSize: '1.05rem', color: '#666', fontWeight: 500 }}>Faster Development</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            fontSize: '4.5rem',
                            fontWeight: 900,
                            background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            marginBottom: '12px'
                        }}>0%</div>
                        <div style={{ fontSize: '1.05rem', color: '#666', fontWeight: 500 }}>Vendor Lock-in</div>
                    </div>
                </div>

                {/* Feature Grid */}
                <div className="row g-4">
                    {features.map((feature, index) => (
                        <div key={index} className="col-md-6">
                            <div
                                style={{
                                    padding: '36px',
                                    background: '#ffffff',
                                    borderRadius: '12px',
                                    borderLeft: `5px solid ${theme.secondary}`,
                                    height: '100%',
                                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                                    transition: 'transform 0.2s, box-shadow 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                                }}
                            >
                                <h4 style={{ fontWeight: 700, color: theme.primary, marginBottom: '14px', fontSize: '1.35rem' }}>
                                    {feature.title}
                                </h4>
                                <p style={{ color: '#666', margin: 0, lineHeight: 1.7, fontSize: '1.05rem' }}>
                                    {feature.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default PlatformSection;
